/**
 * Local draft driver for single-player. Holds a DraftState in a reactive store,
 * applies the human's picks, and — after a short visible pause on the computer's
 * turn — makes the AI's pick via the pure `chooseDraftPick`. The pure draft
 * engine is the single source of truth; this is the thin reactive glue, mirroring
 * src/app/ai.ts. The online session implements the same `DraftBackend` so the
 * DraftScreen UI is identical in both modes.
 */
import { createSignal, onCleanup } from "solid-js";
import { createStore, reconcile } from "solid-js/store";
import {
  chooseDraftPick,
  createDraft,
  draftPass,
  draftPick,
  draftToBuilds,
  type CardTypeId,
  type DeckBuild,
  type DraftResult,
  type DraftState,
  type PlayerId,
} from "../core";

/** The minimal surface the DraftScreen needs from whatever drives the draft. */
export interface DraftBackend {
  readonly state: DraftState;
  /** The seat the viewer controls, or null when spectating. */
  readonly mySeat: PlayerId | null;
  /** True while the computer is deliberating (single-player only). */
  readonly thinking?: () => boolean;
  pick(card: CardTypeId): void;
  pass(): void;
}

export interface DraftRunnerOptions {
  readonly aiSeat: PlayerId;
  readonly thinkMs?: number;
  /**
   * How long to hold on the finished draft before handing off, so the player can
   * admire the army they built (the "muster" reveal) before the board mounts.
   */
  readonly revealMs?: number;
  /**
   * Called once when the draft finishes, with both seats' decks. Invoked from a
   * pick/pass (an event handler or the AI's timer), NOT from a reactive effect —
   * so the game/controller it creates is owned by a stable root, not a
   * computation that gets disposed.
   */
  readonly onComplete?: (builds: [DeckBuild, DeckBuild]) => void;
}

export function createDraftRunner(seed: number, options: DraftRunnerOptions): DraftBackend {
  const aiSeat = options.aiSeat;
  const mySeat: PlayerId = aiSeat === 0 ? 1 : 0;
  const thinkMs = options.thinkMs ?? 500;
  const revealMs = options.revealMs ?? 1300;

  const [store, setStore] = createStore<{ state: DraftState }>({ state: createDraft(seed) });
  const [thinking, setThinking] = createSignal(false);
  let completed = false;
  let revealTimer: ReturnType<typeof setTimeout> | undefined;

  const apply = (r: DraftResult) => {
    if (!r.ok) return;
    setStore("state", reconcile(r.state));
    if (r.state.phase === "done" && !completed) {
      completed = true;
      // Hold on the finished draft for a beat (the "muster" reveal) before
      // handing the decks off. Fired from a timer, not a reactive effect, so the
      // controller beginGame creates isn't owned by a disposed computation.
      const builds = draftToBuilds(r.state);
      revealTimer = setTimeout(() => options.onComplete?.(builds), revealMs);
    }
  };

  // The computer drafts on its turns until the draft completes. We can't drive
  // this off a reactive effect: after the human has finished, the AI takes many
  // consecutive picks and the turn never changes, so nothing would re-trigger.
  // Instead chain a self-scheduling timer that keeps picking while it's the AI's
  // turn, then re-arms whenever a human action hands the turn back.
  let timer: ReturnType<typeof setTimeout> | undefined;
  function scheduleAi(): void {
    if (timer !== undefined) return; // a pick is already pending
    const s = store.state;
    if (s.phase !== "drafting" || s.turn !== aiSeat) {
      setThinking(false);
      return;
    }
    setThinking(true);
    timer = setTimeout(() => {
      timer = undefined;
      const cur = store.state;
      if (cur.phase === "drafting" && cur.turn === aiSeat) {
        const card = chooseDraftPick(cur, aiSeat);
        apply(card ? draftPick(cur, aiSeat, card) : draftPass(cur, aiSeat));
      }
      scheduleAi(); // keep going while it's still the AI's turn
    }, thinkMs);
  }
  onCleanup(() => {
    if (timer !== undefined) clearTimeout(timer);
    if (revealTimer !== undefined) clearTimeout(revealTimer);
  });

  // If the computer drafts first (aiSeat 0), get it going immediately.
  scheduleAi();

  return {
    get state() {
      return store.state;
    },
    mySeat,
    thinking,
    pick(card: CardTypeId) {
      if (store.state.turn === mySeat) {
        apply(draftPick(store.state, mySeat, card));
        scheduleAi(); // turn may have passed to the computer
      }
    },
    pass() {
      if (store.state.turn === mySeat) {
        apply(draftPass(store.state, mySeat));
        scheduleAi(); // computer now drafts the rest alone
      }
    },
  };
}
